import { ReactElement, FC, useEffect, useMemo } from 'react'
import { Form } from 'react-router-dom'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../core/redux/hooks'
import { getAllAgencies } from '../../agencies/slices'
import { RoleType } from '../../core/routes/constants'
import { getCategories } from '../../campaign-categories/slices'
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem
} from '@mui/material'
import { CampaignListParams } from '../../campaigns/interfaces/campaigns'
import {
  AgenciesSelectItems,
  allText,
  onCategorySelectChange,
  selectMenuItem
} from '../../commons/utils/utils'
import { chip } from '../../campaigns/utils'
import SelfServeSearchField from '../../ui/search-field/SelfServeSearchField'
import SelfServeCustomSelect from '../../ui/select/SelfServeCustomSelect'

interface WalletTransactionsListHeaderProps {
  handleFormChange: (changedData: CampaignListParams) => void
  search?: string
  agency?: string
  categories: string[]
}

const WalletTransactionsListHeader: FC<WalletTransactionsListHeaderProps> = ({
  handleFormChange,
  search,
  agency,
  categories
}): ReactElement => {
  const { t } = useTranslation()

  const {
    agencies: { agencies },
    auth: { userType, tenantID, isAgencyEnabled },
    campaignCategories
  } = useAppSelector((state) => state)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (userType !== RoleType.AGENCY && isAgencyEnabled) {
      void dispatch(getAllAgencies({ tenantID }))
    }
    void dispatch(getCategories({ tenantID, agencyID: agency }))
  }, [tenantID, dispatch, userType, agency, isAgencyEnabled])

  const categoriesSelectItems = useMemo(
    () =>
      campaignCategories.categories.map((category) => (
        <MenuItem
          className='multi-select-item'
          disableRipple
          disableTouchRipple
          key={category.CampaignCategoryID}
          value={category.CampaignCategoryID?.toString()}
        >
          <Checkbox
            disableRipple
            disableFocusRipple
            disableTouchRipple
            checked={categories.includes(
              category.CampaignCategoryID?.toString() ?? ''
            )}
          />
          <ListItemText primary={category.CategoryName} />
        </MenuItem>
      )),
    [campaignCategories.categories, categories]
  )

  return (
    <div>
      <Form role='search'>
        <FormControl className='form-control'>
          <SelfServeSearchField value={search} onChange={handleFormChange} />
        </FormControl>
        {userType !== RoleType.AGENCY && isAgencyEnabled && (
          <FormControl className='form-control' size='small'>
            <InputLabel shrink={false} id='agencies-label'>
              {(agency == null || agency === '') && allText(t`AgencyForSelect`)}
            </InputLabel>
            <SelfServeCustomSelect
              handleFormChange={(e) =>
                handleFormChange({ agencyID: e.target.value as string })}
              value={agency}
              menuItem={selectMenuItem(t`AllAgencies`)}
              selectItems={AgenciesSelectItems(agencies)}
              inputProps='table-input maxw-8 minw-8'
            />
          </FormControl>
        )}
        <FormControl size='small' className='form-control'>
          {categories?.length === 0 && (
            <InputLabel shrink={false} id='categories-label'>
              {(categories == null || categories?.length === 0) &&
                allText(t`CategoriesSelect`)}
            </InputLabel>
          )}
          <SelfServeCustomSelect
            handleFormChange={(e) =>
              onCategorySelectChange(e, handleFormChange)}
            value={categories}
            menuItem={selectMenuItem(t`AllCategories`)}
            selectItems={categoriesSelectItems}
            inputProps='table-input minw-7'
            native={false}
            displayEmpty
            renderValue={() => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {categories.map((value) =>
                  chip(value, campaignCategories.categories)
                )}
              </Box>
            )}
            multiple
          />
        </FormControl>
      </Form>
    </div>
  )
}

export default WalletTransactionsListHeader
